/**
 * Base URL:
 * - `VITE_API_URL` được Vite inline lúc build — đặt trong `apps/web/.env` khi API
 *   không chạy ở localhost.
 * - Mặc định là localhost:3001/api (PORT trong apps/api/.env + tiền tố `api`
 *   đặt ở apps/api/src/main.ts).
 *
 * Mirror của apps/mobile/src/api/client.ts, khác hai điểm:
 * - token do AuthContext của web nạp từ localStorage thay vì SecureStore;
 * - lỗi lấy `message` từ body Nest trả về để hiện đúng câu tiếng Việt cho người dùng,
 *   thay vì chuỗi kỹ thuật "POST /auth/login failed with 401".
 */
const DEFAULT_BASE_URL = 'http://localhost:3001/api';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? DEFAULT_BASE_URL;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Lỗi mạng / API không chạy — tách riêng để màn hình nói đúng nguyên nhân. */
export class NetworkError extends Error {
  constructor(message = 'Không kết nối được máy chủ, thử lại sau.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/// Token hiện tại, set bởi AuthContext sau login/logout/khôi phục từ localStorage.
/// Biến module-level là đủ — chưa có refresh token nên không cần interceptor.
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

/** Cho kết nối Socket.IO của Chat — gửi token trong handshake. */
export function getAuthToken(): string | null {
  return authToken;
}

/** Nest trả lỗi dạng { statusCode, message, error }; message có thể là string hoặc string[]. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message: unknown }).message;
      if (typeof m === 'string') return m;
      if (Array.isArray(m) && m.length) return String(m[0]);
    }
  } catch {
    // Body không phải JSON — rơi xuống thông báo mặc định bên dưới.
  }
  return res.status === 401 ? 'Phiên đăng nhập đã hết hạn.' : 'Có lỗi xảy ra, thử lại sau.';
}

async function request<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new NetworkError();
  }
  if (!res.ok) {
    throw new ApiError(res.status, await errorMessage(res));
  }
  return res.json() as Promise<T>;
}

export const apiGet = <T,>(path: string): Promise<T> => request<T>('GET', path);
export const apiPost = <T,>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body);
export const apiPatch = <T,>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body);
export const apiDelete = <T,>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body);

/** POST multipart (gửi ảnh). Không tự đặt Content-Type: trình duyệt tự thêm boundary cho FormData. */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      body: form,
    });
  } catch {
    throw new NetworkError();
  }
  if (!res.ok) {
    throw new ApiError(res.status, await errorMessage(res));
  }
  return res.json() as Promise<T>;
}

/// Đường dẫn ảnh API trả về tương đối với gốc API (`/media/...`); blob:/data: (dữ liệu giả) giữ nguyên.
export function mediaUrl(path: string): string {
  return /^(https?|blob|data):/.test(path) ? path : `${API_BASE_URL}${path}`;
}
